import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ADMIN_SITE_URL } from '../utils/api';
import { usePublicConfig, useGivingState, sheetOn, openGiveFromClick, SUPPORT_LEAD } from '../utils/giving';
import { TRACK_OPEN } from '../utils/giveCore';
import { useOnList } from '../utils/emailList';
import { useVoiceReplies, toggleVoiceReplies } from '../utils/voicePreference';
import { useTeamVoice, setTeamVoice, TEAM_VOICES } from '../utils/teamVoice';
import { useTeamFirstAnswerPick, setTeamFirstAnswerPick, FA_PICKS, FA_PICK_NAMES } from '../utils/experiments';
import { BOOK_TITLE, BOOK_AMAZON_URL, THE_NET_SOCIALS } from '../utils/book';
import Icon from './Icon';
import { AprLockup, AprMark } from './BrandMarks';
import { StayInTheLoopDialog } from './EmailSignup';
import { CrisisLine } from './ChatParts';
import './Sidebar.css';

function chatTitle(chat) {
  if (chat.title) return chat.title.length > 44 ? chat.title.slice(0, 44) + '…' : chat.title;
  if (chat.videoTitle) return chat.videoTitle;
  const firstUser = (chat.messages || []).find((m) => m.role === 'user');
  const text = firstUser?.content || 'Conversation';
  return text.length > 44 ? text.slice(0, 44) + '…' : text;
}

function chatWhen(chat) {
  const ts = chat.lastUpdated || chat.createdAt;
  if (!ts) return '';
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  const days = Math.floor((Date.now() - date.getTime()) / 86400000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const PICK_NAMES = { grok: 'Grok', elevenlabs: 'ElevenLabs' };

/**
 * Rob's voice, for the team only (utils/teamVoice.js): a signed-in owner, admin or officer picks
 * Grok Rob or ElevenLabs Rob for every answer read aloud on this browser, outside the voice test.
 * The server checks the role on every request; a visitor's browser never shows this. Two radio
 * buttons joined like the answer-style pick; an arrow key moves to the other one.
 */
function TeamVoicePick({ voice }) {
  const refs = useRef({});
  const onKeyDown = (e) => {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) return;
    e.preventDefault();
    const next = TEAM_VOICES[(TEAM_VOICES.indexOf(voice) + 1) % TEAM_VOICES.length];
    setTeamVoice(next);
    if (refs.current[next]) refs.current[next].focus();
  };
  return (
    <div className="sidebar-voice-pick" role="radiogroup" aria-labelledby="apr-team-voice-label" aria-describedby="apr-team-voice-note" onKeyDown={onKeyDown}>
      <span className="sidebar-voice-pick-label">
        <span id="apr-team-voice-label">Rob’s voice</span>
        <small id="apr-team-voice-note">Team only. Visitors hear what the voice test gives them.</small>
      </span>
      <span className="sidebar-voice-options">
        {TEAM_VOICES.map((v) => (
          <button
            key={v}
            ref={(el) => { refs.current[v] = el; }}
            type="button"
            role="radio"
            aria-checked={voice === v}
            tabIndex={voice === v ? 0 : -1}
            className={`sidebar-voice-option${voice === v ? ' on' : ''}`}
            onClick={() => setTeamVoice(v)}
            data-track={`Team voice: ${PICK_NAMES[v]}`}
          >
            {PICK_NAMES[v]}
          </button>
        ))}
      </span>
    </div>
  );
}

/**
 * First Answer, for the team only (utils/experiments.js): a signed-in owner, admin or officer gets
 * today's full first answer, never the test's groups, unless they pick Short + More here to see it
 * for themselves (the server checks the role; it is never counted). The voice pick's look and keys.
 */
function TeamFirstAnswerPick({ pick }) {
  const refs = useRef({});
  const onKeyDown = (e) => {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) return;
    e.preventDefault();
    const next = FA_PICKS[(FA_PICKS.indexOf(pick) + 1) % FA_PICKS.length];
    setTeamFirstAnswerPick(next);
    if (refs.current[next]) refs.current[next].focus();
  };
  return (
    <div className="sidebar-voice-pick" role="radiogroup" aria-labelledby="apr-team-fa-label" aria-describedby="apr-team-fa-note" onKeyDown={onKeyDown}>
      <span className="sidebar-voice-pick-label">
        <span id="apr-team-fa-label">First answer</span>
        <small id="apr-team-fa-note">Team only. Visitors get what the First Answer test gives them.</small>
      </span>
      <span className="sidebar-voice-options">
        {FA_PICKS.map((p) => (
          <button
            key={p}
            ref={(el) => { refs.current[p] = el; }}
            type="button"
            role="radio"
            aria-checked={pick === p}
            tabIndex={pick === p ? 0 : -1}
            className={`sidebar-voice-option${pick === p ? ' on' : ''}`}
            onClick={() => setTeamFirstAnswerPick(p)}
            data-track={`Team first answer: ${FA_PICK_NAMES[p]}`}
          >
            {FA_PICK_NAMES[p]}
          </button>
        ))}
      </span>
    </div>
  );
}

// Wide screens: the menu folds to a rail of icons (team review, Oct 2026).
// Folded until someone opens it; the choice is kept per browser.
const RAIL_KEY = 'apr-menu-folded';

function readFolded() {
  try {
    const v = localStorage.getItem(RAIL_KEY);
    return v == null ? true : v === '1';
  } catch (e) {
    return true;
  }
}

/**
 * The menu: new conversation, the two ways to ask, saved conversations,
 * light mode, the account, the team portal link (role-gated), support (one
 * quiet line, for everyone, ad visitors included, that opens the support
 * sheet; during a crisis hold the 988 line stands in its place:
 * utils/giving.js), "Stay in the loop" (the email list), Pastor Rob's book,
 * The Net's channels and the policies.
 * Wide screens: a rail that folds to icons, each with its name on hover and
 * as its accessible name. Phones: a panel from the menu button's side.
 */
function Sidebar({
  user,
  myRole,
  chats,
  activeChatId,
  currentView,
  unseen,
  hasFocused,
  open,
  onClose,
  onNewChat,
  onSelectChat,
  onDeleteChat,
  onOpenChat,
  onFocusedStudy,
  onResumeFocused,
  onAccount,
  onContact,
  onRequestSignIn,
  onSignOut,
  lightReading = false,
  onToggleLightReading,
}) {
  const isGuest = Boolean(user?.isAnonymous);
  const unseenArchive = Boolean(unseen && unseen.archive);
  const unseenFocused = Boolean(unseen && unseen.focused);
  const closeRef = useRef(null);
  const loopRef = useRef(null);
  const voiceOn = useVoiceReplies();
  // The team's pick of voice (utils/teamVoice.js): null for everyone getMyRole did not name.
  const teamVoice = useTeamVoice();
  // And of first answer (utils/experiments.js): null for everyone else, too.
  const teamFirstAnswer = useTeamFirstAnswerPick();
  const publicConfig = usePublicConfig();
  const { giveUrl } = publicConfig;
  // Resting never hides the menu's ask; a crisis hold does (utils/giving.js).
  const givingHeld = useGivingState().ask === 'hold';
  // With the support sheet on, a plain tap opens it; on a phone the panel
  // steps aside first, and focus comes back to the menu button after.
  const opensSheet = (surface) => (sheetOn(publicConfig) ? {
    'aria-haspopup': 'dialog',
    'data-give-opener': surface,
    'data-track': TRACK_OPEN[surface],
    onClick: (e) => { if (openGiveFromClick(e, { surface }) && open) onClose(); },
  } : {});
  const onList = useOnList();
  const [loopOpen, setLoopOpen] = useState(false);
  // "Stay in the loop": on a phone the panel steps aside for the dialog.
  const openLoop = (e) => {
    loopRef.current = e.currentTarget;
    if (open) onClose();
    setLoopOpen(true);
  };
  const closeLoop = useCallback(() => {
    setLoopOpen(false);
    const opener = loopRef.current;
    if (opener && opener.getClientRects().length) opener.focus({ preventScroll: true });
  }, []);
  const [folded, setFolded] = useState(readFolded);
  const toggleFolded = () => {
    setFolded((was) => {
      try { localStorage.setItem(RAIL_KEY, was ? '0' : '1'); } catch (e) { /* private window: just this visit */ }
      return !was;
    });
  };

  // Opening the panel moves focus into it; Escape closes it.
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => closeRef.current?.focus({ preventScroll: true }), 50);
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => { clearTimeout(t); window.removeEventListener('keydown', onKey); };
  }, [open, onClose]);

  const inStudy = ['focused', 'multi', 'single'].includes(currentView);
  // Contact us is a real link (a new tab works) that opens in place on a plain click.
  const openContact = (e) => {
    if (!onContact || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    onContact();
  };

  return (
    <>
      {open && <div className="sidebar-backdrop" onClick={onClose} aria-hidden="true" />}
      <aside id="apr-menu" className={`sidebar${open ? ' open' : ''}${folded ? ' folded' : ''}`} aria-label="Menu">
        <div className="sidebar-brand">
          <span className="sidebar-lockup"><AprLockup height={22} /></span>
          <button
            type="button"
            className="sidebar-fold"
            onClick={toggleFolded}
            aria-label={folded ? 'Expand menu' : 'Collapse menu'}
            aria-expanded={!folded}
            data-tip={folded ? 'Expand menu' : 'Collapse menu'}
          >
            {/* Folded, the rail's top is the mark; hover or focus shows what it does. */}
            <span className="sidebar-fold-mark"><AprMark height={22} /></span>
            <Icon name={folded ? 'panel-left-open' : 'panel-left-close'} size={20} className="sidebar-fold-icon" />
          </button>
          <button ref={closeRef} type="button" className="sidebar-close" onClick={onClose} aria-label="Close menu">
            <Icon name="x" size={24} />
          </button>
        </div>

        <button type="button" className="sidebar-new-chat" onClick={onNewChat} data-tip="New conversation">
          <Icon name="plus" size={20} />
          <span className="rail-words">New conversation</span>
        </button>

        <nav className="sidebar-nav" aria-label="Ask Pastor Rob">
          <button
            type="button"
            className={`sidebar-nav-item ${currentView === 'chat' ? 'active' : ''}`}
            onClick={onOpenChat}
            aria-current={currentView === 'chat' ? 'page' : undefined}
            aria-label={unseenArchive ? 'Ask anything. A new answer is waiting' : undefined}
            data-tip="Ask anything"
          >
            <Icon name="message-square" size={20} />
            <span className="nav-text">
              Ask anything
              <small className="nav-hint">Searches all of Rob’s sermons</small>
            </span>
            {unseenArchive && <span className="nav-dot" aria-hidden="true" />}
          </button>
          <button
            type="button"
            className={`sidebar-nav-item ${inStudy ? 'active' : ''}`}
            onClick={onFocusedStudy}
            aria-current={inStudy ? 'page' : undefined}
            aria-label={unseenFocused ? 'Study specific sermons. A new answer is waiting' : undefined}
            data-tip="Study specific sermons"
          >
            <Icon name="book-open" size={20} />
            <span className="nav-text">
              Study specific sermons
              <small className="nav-hint">Pick one or more, then ask</small>
            </span>
            {unseenFocused && <span className="nav-dot" aria-hidden="true" />}
          </button>
          {hasFocused && onResumeFocused && (
            <button
              type="button"
              className={`sidebar-nav-item sub ${['multi', 'single'].includes(currentView) ? 'active' : ''}`}
              onClick={onResumeFocused}
            >
              <Icon name="chevron-right" size={16} />
              <span className="nav-text">Back to your sermon study</span>
              {unseenFocused && <span className="nav-dot" aria-hidden="true" />}
            </button>
          )}
          {myRole && (
            <a className="sidebar-nav-item" href={ADMIN_SITE_URL} target="_blank" rel="noopener noreferrer" data-tip="Team portal">
              <Icon name="users" size={20} />
              <span className="nav-text">Team portal</span>
              <Icon name="external-link" size={16} className="nav-ext" />
            </a>
          )}
        </nav>

        <div className="sidebar-section-label">Saved conversations</div>
        <div className="sidebar-chats">
          {isGuest ? (
            <div className="sidebar-guest-hint">
              <p>Sign in to keep your conversations in this list and come back to them on any device.</p>
              <button type="button" className="apr-btn apr-btn--quiet sidebar-signin-btn" onClick={onRequestSignIn}>
                Sign in
              </button>
            </div>
          ) : chats.length === 0 ? (
            <div className="sidebar-empty">Saved conversations will appear here. Use “Save chat” under the box to keep one.</div>
          ) : (
            chats.map((chat) => (
              <div
                key={chat.id}
                className={`sidebar-chat ${chat.id === activeChatId ? 'active' : ''}`}
              >
                <button
                  type="button"
                  className="sidebar-chat-main"
                  onClick={() => onSelectChat(chat)}
                  title={chatTitle(chat)}
                  aria-current={chat.id === activeChatId ? 'true' : undefined}
                >
                  <span className="sidebar-chat-title">{chatTitle(chat)}</span>
                  <span className="sidebar-chat-when">{chatWhen(chat)}</span>
                </button>
                <button
                  type="button"
                  className="sidebar-chat-delete"
                  onClick={() => onDeleteChat(chat)}
                  aria-label={`Delete conversation: ${chatTitle(chat)}`}
                  title="Delete conversation"
                >
                  <Icon name="x" size={16} />
                </button>
              </div>
            ))
          )}
        </div>

        <div className="sidebar-footer">
          <button
            type="button"
            className="sidebar-setting"
            role="switch"
            aria-checked={voiceOn}
            aria-describedby="apr-voice-setting-note"
            onClick={toggleVoiceReplies}
            data-tip={voiceOn ? 'Read answers aloud: on' : 'Read answers aloud: off'}
            data-track={voiceOn ? 'Read aloud: turn off' : 'Read aloud: turn on'}
          >
            <Icon name={voiceOn ? 'volume-2' : 'volume-x'} size={20} />
            <span className="nav-text">
              Read aloud
              <small id="apr-voice-setting-note">Answers, in an AI version of Rob’s voice</small>
            </span>
            <span className="apr-switch-track" aria-hidden="true" />
          </button>
          {teamVoice && <TeamVoicePick voice={teamVoice} />}
          {teamFirstAnswer && <TeamFirstAnswerPick pick={teamFirstAnswer} />}
          {onToggleLightReading && (
            <button
              type="button"
              className="sidebar-setting"
              role="switch"
              aria-checked={lightReading}
              onClick={onToggleLightReading}
              data-tip={lightReading ? 'Light mode: on' : 'Light mode: off'}
            >
              <Icon name="sun" size={20} />
              <span className="nav-text">Light mode</span>
              <span className="apr-switch-track" aria-hidden="true" />
            </button>
          )}

          {/* The folded rail's account and support: one icon each. */}
          <button
            type="button"
            className="sidebar-rail-btn rail-only"
            onClick={isGuest ? onRequestSignIn : onAccount}
            aria-label={isGuest ? 'Sign in' : 'Account'}
            data-tip={isGuest ? 'Sign in' : 'Account'}
          >
            <Icon name="circle-user-round" size={20} />
          </button>
          {!givingHeld && (
            <a
              className="sidebar-rail-btn rail-only"
              href={giveUrl}
              target="_blank"
              rel="noopener"
              aria-label="Support Ask Pastor Rob (donate)"
              data-tip="Support Ask Pastor Rob"
              {...opensSheet('rail')}
            >
              <Icon name="heart" size={20} />
            </a>
          )}
          <a
            className="sidebar-rail-btn rail-only"
            href={BOOK_AMAZON_URL}
            target="_blank"
            rel="noopener"
            aria-label="Pastor Rob’s book, Genius (Amazon)"
            data-tip="Pastor Rob’s book, Genius"
            data-track="Get the book on Amazon"
          >
            <Icon name="book" size={20} />
          </a>
          <button
            type="button"
            className="sidebar-rail-btn rail-only"
            onClick={openLoop}
            aria-label={onList ? 'You’re on the list' : 'Get updates'}
            data-tip={onList ? 'You’re on the list' : 'Get updates'}
          >
            <Icon name="mail" size={20} />
          </button>

          <a
            className="sidebar-rail-btn rail-only"
            href="/contact"
            onClick={openContact}
            aria-current={currentView === 'contact' ? 'page' : undefined}
            aria-label="Contact us"
            data-tip="Contact us"
          >
            <Icon name="contact-round" size={20} />
          </a>

          <div className="sidebar-user">
            {isGuest ? (
              <>
                <span className="sidebar-user-name">Guest</span>
                <button type="button" className="sidebar-user-action strong" onClick={onRequestSignIn}>Sign in</button>
              </>
            ) : (
              <>
                <button type="button" className="sidebar-user-name as-button" onClick={onAccount} title="Account settings" data-no-track>
                  {user.displayName?.trim() || user.email}
                </button>
                <button type="button" className="sidebar-user-action" onClick={onAccount}>Account</button>
                <button type="button" className="sidebar-user-action" onClick={onSignOut}>Sign out</button>
              </>
            )}
          </div>

          {/* Always here, never in the way: the ask lives in one quiet line.
              During a crisis hold, the way to a person stands in its place. */}
          {givingHeld ? (
            <div className="sidebar-give sidebar-give--crisis">
              <CrisisLine />
            </div>
          ) : (
            <p className="sidebar-give">
              <strong className="sidebar-give-title">Support Ask Pastor Rob</strong>
              {SUPPORT_LEAD} <a className="sidebar-give-cta" href={giveUrl} target="_blank" rel="noopener" {...opensSheet('menu')}>donate here</a>.
            </p>
          )}
          {onList ? (
            <p className="sidebar-loop done">
              <Icon name="check" size={16} />
              <span>You’re on the list</span>
            </p>
          ) : (
            <button type="button" className="sidebar-loop" onClick={openLoop}>
              <Icon name="mail" size={16} />
              <span>Stay in the loop</span>
            </button>
          )}

          <p className="sidebar-book">
            <strong className="sidebar-give-title">Pastor Rob’s book</strong>
            <cite className="sidebar-book-title">{BOOK_TITLE}</cite>
            <a className="sidebar-give-cta" href={BOOK_AMAZON_URL} target="_blank" rel="noopener" data-track="Get the book on Amazon">Get it on Amazon</a>
          </p>

          <p className="sidebar-promise">
            Your conversations are private. We never sell them, and we don’t share them with advertisers or anyone outside the services that run Ask Pastor Rob.
          </p>
          <p className="sidebar-follow">
            <span className="sidebar-follow-label">Follow The Net</span>
            {THE_NET_SOCIALS.map(({ label, href }) => (
              <a key={label} href={href} target="_blank" rel="noopener">{label}</a>
            ))}
          </p>
          <div className="sidebar-policies">
            <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy</a>
            <span aria-hidden="true"> · </span>
            <a href="/terms" target="_blank" rel="noopener noreferrer">Terms</a>
            <span aria-hidden="true"> · </span>
            <a href="/contact" onClick={openContact} aria-current={currentView === 'contact' ? 'page' : undefined}>Contact</a>
            <span aria-hidden="true"> · </span>
            <a href="/crisis-help" target="_blank" rel="noopener noreferrer">Crisis help</a>
            <span aria-hidden="true"> · </span>
            <a href="https://www.thenet.org/contact/" target="_blank" rel="noopener noreferrer">The Net</a>
          </div>
          <p className="sidebar-about">
            AI-assisted answers from Pastor Rob’s own sermons, by The Net, a church in downtown Huntsville.{' '}
            <a href="https://www.thenet.org/" target="_blank" rel="noopener noreferrer">thenet.org</a>
          </p>
        </div>
      </aside>
      {loopOpen && <StayInTheLoopDialog onList={onList} onClose={closeLoop} />}
    </>
  );
}

export default Sidebar;
